import createReducer from '../lib/createReducer';
import setPayloadReducer from '../lib/setPayloadReducer';
import {
  getCoordinate,
  getSelectedArea,
  getZoom,
  updateUrl,
} from '../lib/url';
import { getRasterManager } from '../lib/mapService';

const initialState = {
  address: '',
  bounds: {
    east: 0,
    north: 0,
    south: 0,
    west: 0,
  },
  coordinate: getCoordinate(),
  elevation: 0,
  population: -1,
  progress: 0.0,
  selectedArea: getSelectedArea(),
  zoom: getZoom(),
};

const SET_ADDRESS = 'MAP::SET_ADDRESS';
const SET_BOUNDS = 'MAP::SET_BOUNDS';
const SET_COORDINATE = 'MAP::SET_COORDINATE';
const SET_ELEVATION = 'MAP::SET_ELEVATION';
const SET_LOADED = 'MAP::SET_LOADED';
const SET_POPULATION = 'MAP::SET_POPULATION';
const SET_PROGRESS = 'MAP::SET_PROGRESS';
const SET_SELECTED_AREA = 'MAP::SET_SELECTED_AREA';
const SET_ZOOM = 'MAP::SET_ZOOM';

/**
 * Set address.
 * @param {!String} address The address of the location.
 * @return {object} action.
 */
export const setAddress = (address) => ({
  type: SET_ADDRESS,
  payload: { address },
});

/**
 * Set bounds.
 * @param {!object} bounds The bounds of the map.
 * @return {object} action.
 */
export const setBounds = (bounds) => ({
  type: SET_BOUNDS,
  payload: { bounds },
});

/**
 * Set coordinate.
 * @param {!object} coordinate The coordinate of the location.
 * @return {object} action.
 */
export const setCoordinate = (coordinate) => ({
  type: SET_COORDINATE,
  payload: { coordinate },
});

/**
 * Set elevation.
 * @param {!Number} elevation The elevation of the coordinate.
 * @return {object} action.
 */
export const setElevation = (elevation) => ({
  type: SET_ELEVATION,
  payload: { elevation },
});

/**
 * Set population.
 * @param {!Number} population The population of the selected area.
 * @return {object} action.
 */
export const setPopulation = (population) => ({
  type: SET_POPULATION,
  payload: { population },
});

/**
 * Set progress.
 * The value should be set within range [0, 1]
 * @param {!Number} progress The loading progress of the map.
 * @return {object} action.
 */
export const setProgress = (progress) => ({
  type: SET_PROGRESS,
  payload: { progress },
});

/**
 * Set selected area.
 * @param {!object} selectedArea The coordinate of the poupulatoin circle.
 * @param {bool} isUpdateUrl should url be updated.
 * @return {object} action.
 */
export const setSelectedArea = (
    selectedArea,
    isUpdateUrl = true,
) => (dispatch, getState) => {
  dispatch({
    type: SET_SELECTED_AREA,
    payload: { selectedArea },
  });

  if (isUpdateUrl) {
    updateUrl(getState());
  }
};

/**
 * Set zoom.
 * @param {!Number} zoom The zoom of the map.
 * @param {bool} isUpdateUrl should url be updated.
 * @return {object} action.
 */
export const setZoom = (zoom, isUpdateUrl = true) => (dispatch, getState) => {
  dispatch({
    type: SET_ZOOM,
    payload: { zoom },
  });

  if (isUpdateUrl) {
    updateUrl(getState());
  }

  const rasterManager = getRasterManager();
  if (rasterManager) {
    rasterManager.setBlur(zoom < 9 ? 3 : 5);
  }
};

const reducer = {
  [SET_ADDRESS]: setPayloadReducer,
  [SET_BOUNDS]: setPayloadReducer,
  [SET_ELEVATION]: setPayloadReducer,
  [SET_LOADED]: setPayloadReducer,
  [SET_POPULATION]: setPayloadReducer,
  [SET_PROGRESS]: setPayloadReducer,
  [SET_ZOOM]: setPayloadReducer,

  [SET_COORDINATE]: (state, action) => {
    const { coordinate } = action.payload;

    if (!coordinate) {
      return state;
    }

    if (state.coordinate.lat === coordinate.lat
      && state.coordinate.lng === coordinate.lng) {
      return state;
    }

    return {
      ...state,
      coordinate,
    };
  },

  [SET_SELECTED_AREA]: (state, action) => {
    const { selectedArea } = action.payload;

    if (selectedArea === null && state.selectedArea === null) {
      return state;
    }

    if (selectedArea !== null
      && state.selectedArea !== null
      && state.selectedArea.lat === selectedArea.lat
      && state.selectedArea.lng === selectedArea.lng
      && state.selectedArea.radius === selectedArea.radius) {
      return state;
    }

    return {
      ...state,
      selectedArea,
    };
  },
};

export default createReducer(reducer, initialState);
